import { createAsyncThunk } from '@reduxjs/toolkit'
import railsApiService from 'services/api/rails-api-service'
import { Consultation } from 'types'
import { CuideiAttribution, CuideiLeadForm } from 'types/cuidei'
import { formatCuideiIdentityDocument, onlyDigits } from 'utils/cuidei'
import { CuideiAnswersPayload } from 'utils/cuideiPathology'

interface CreateCuideiConsultationParams {
  /** /farma sends everything; the pathology flow sends the identity document + address after payment. */
  patientForm: CuideiLeadForm
  attribution: CuideiAttribution | null
  /** Pathology landings only: slug + answers snapshot input (see utils/cuideiPathology). */
  pathology?: CuideiAnswersPayload
}

/**
 * Creates a Cuidei live (waiting-line) consultation from the Step 1 form data.
 *
 * Reuses the existing live-attendance backend path (is_live → doctorless,
 * enters the `waiting` queue, claimed by an available doctor) but flags the
 * consultation as Cuidei so the backend applies the R$79 generalist product and
 * binds the pharmacy attribution for routing + commission. The patient has no
 * account yet at this point (created later, post-payment) — the lead's details
 * travel in patient_attributes, mirroring the small-form live path.
 */
export const createCuideiConsultation = createAsyncThunk(
  'cuidei/create-consultation',
  async ({ patientForm, attribution, pathology }: CreateCuideiConsultationParams, { rejectWithValue }) => {
    try {
      const completeName = `${patientForm.firstName} ${patientForm.lastName}`.trim()
      const cpf = onlyDigits(patientForm.cpf)
      // Backend validates with phony (validates_plausible_phone) and has no
      // default country, so the number must carry Brazil's 55 country code.
      const nationalPhone = onlyDigits(patientForm.phone)
      const phone = nationalPhone.startsWith('55') ? nationalPhone : `55${nationalPhone}`

      const payload = {
        consultation: {
          is_live: true,
          is_cuidei: true,
          pharmacy_id: attribution?.pharmacyId ?? null,
          attribution: attribution
            ? {
                pharmacy_id: attribution.pharmacyId,
                utm_source: attribution.utmSource,
                utm_medium: attribution.utmMedium,
                utm_campaign: attribution.utmCampaign,
                session_id: attribution.sessionId,
                landing_at: attribution.landingAt
              }
            : null,
          // Pathology landings (cuidei.com.br/<slug>): the backend validates the
          // answers against the current questions version before creating anything.
          ...(pathology ? { cuidei_pathology_slug: pathology.slug, cuidei_answers: pathology.answers } : {}),
          patient_attributes: {
            first_name: patientForm.firstName,
            last_name: patientForm.lastName,
            complete_name: completeName,
            email: patientForm.email,
            cpf_number: cpf,
            ssn: cpf,
            phone_number: phone,
            // Identity document (internal records) + birth date — required on a
            // valid controlled prescription. Same DD/MM/AAAA format the
            // live/medleve flow submits. Foreigners use RNM/passaporte — the
            // type rides prefixed inside rg_number (free-form varchar) so the
            // doctor and the receita show the right document kind.
            ...(patientForm.rg ? { rg_number: formatCuideiIdentityDocument(patientForm.docType, patientForm.rg) } : {}),
            birth_date: patientForm.birthDate,
            prescription_sex: patientForm.prescriptionSex,
            terms_of_service: true,
            cgu_accepted: true,
            // Address resolved from CEP + number (spec §7, §24). Without a CEP
            // (pathology flow) the backend keeps a placeholder until the final step.
            ...(patientForm.cep
              ? {
                  cep: onlyDigits(patientForm.cep),
                  address_street: patientForm.street ?? null,
                  address_number: patientForm.number,
                  address_complement: patientForm.complement ?? null,
                  neighborhood: patientForm.neighborhood ?? null,
                  city: patientForm.city ?? null,
                  state: patientForm.state ?? null
                }
              : {})
          }
        }
      }

      const { data } = await railsApiService.apiInstance.post<Consultation>('/api/v1/consultations', payload)
      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)
